import { useEffect, useState } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Button } from '@/components/ui/button';
import { SubmitDealStepProps } from '@/types/submit-deal.types';
import type { DealOwner } from '@/types/submit-deal.types';
import { Info, Plus, Trash2 } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

// AJOUTÉ — isPublicMode optionnel, transmis par SubmitDealForm.tsx.
// Ajouté ici en intersection de type plutôt que dans SubmitDealStepProps
// partagé, pour ne rien changer aux autres étapes qui n'en ont pas besoin.
type StepGeneralProps = SubmitDealStepProps & { isPublicMode?: boolean };

export const StepGeneral = ({ formData, updateFormData, errors, clearError, t, isPublicMode }: StepGeneralProps) => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const isFr = language === 'fr';
  const [isBroker, setIsBroker] = useState(false);
  const [representedSellers, setRepresentedSellers] = useState<Array<{ id: string; name: string; email: string }>>([]);

  useEffect(() => {
    const init = async () => {
      // AJOUTÉ — en mode public (lien partagé avec un vendeur précis),
      // ce vendeur n'a jamais à choisir ni voir un "vendeur représenté"
      // — c'est déjà lui, fixé par le lien (publicDraftMeta.repId,
      // préremplit côté SubmitDealForm.tsx). Avant ce correctif, si la
      // personne qui ouvrait ce lien se trouvait par ailleurs connectée
      // avec un compte ayant le rôle broker (le sien, sans lien avec le
      // courtier qui a envoyé CE lien), ce bloc interrogeait ET
      // AFFICHAIT la liste de SES PROPRES vendeurs représentés — une
      // fuite de confidentialité — et réécrivait même
      // formData.general.brokerId avec son propre uid, réassignant
      // silencieusement le deal au mauvais courtier. Coupé ici, à la
      // racine (l'effet lui-même, avant toute requête ou écriture),
      // plutôt que seulement à l'affichage plus bas.
      if (isPublicMode) return;
      if (!user) return;
      const roles: string[] = (user as any).roles || [];
      const role: string = (user as any).role || '';
      const userIsBroker = roles.includes('broker') || role === 'broker';
      setIsBroker(userIsBroker);
      if (!userIsBroker) return;
      updateFormData('general', { brokerId: user.id });
      try {
        const snap = await getDocs(
          query(collection(db, 'representedSellers'), where('brokerId', '==', user.id))
        );
        setRepresentedSellers(
          snap.docs.map(d => ({
            id: d.id,
            name: d.data().name || '',
            email: d.data().email || '',
          }))
        );
      } catch (e) {
        console.error('Error loading represented sellers:', e);
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isPublicMode]);

  const handleChange = (field: string, value: string | number | boolean) => {
    updateFormData('general', { [field]: value });
    clearError(field);
  };

  // ── AJOUTÉ : gestion du répéteur de propriétaires ────────────────────────
  const owners: DealOwner[] = formData.general.owners || [];

  const addOwner = () => {
    const newOwner: DealOwner = {
      id: `owner_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      name: '',
      ownershipPercent: 0,
      role: '',
      email: '',
      phone: '',
    };
    updateFormData('general', { owners: [...owners, newOwner] });
  };

  const updateOwner = (id: string, field: keyof DealOwner, value: string | number) => {
    updateFormData('general', {
      owners: owners.map(o => (o.id === id ? { ...o, [field]: value } : o)),
    });
  };

  const removeOwner = (id: string) => {
    updateFormData('general', { owners: owners.filter(o => o.id !== id) });
  };

  const totalOwnershipPercent = owners.reduce((sum, o) => sum + (Number(o.ownershipPercent) || 0), 0);

  
  // CORRIGÉ (10 sept 2026) — liste unique et complète, sans condition sur
  // locationCountry. Ce champ apparaît PLUS BAS dans le formulaire (étape
  // 1, section localisation), donc il est toujours vide au moment où
  // Entity Type est affiché en haut de la même page — la logique
  // conditionnelle précédente retombait donc systématiquement sur la
  // liste US par défaut, peu importe le vrai pays du vendeur. Regrouper
  // toutes les options en une seule liste, organisées par pays/région
  // via des en-têtes visuels (optgroup-like), est plus simple et robuste
  // qu'une dépendance fragile entre deux champs non liés visuellement.
  const getEntityTypeOptions = () => [
    // USA
    { value: 'llc', label: `${t.step1.entityTypes.llc} (USA)` },
    { value: 'cCorp', label: `${t.step1.entityTypes.cCorp} (USA)` },
    { value: 'sCorp', label: `${t.step1.entityTypes.sCorp} (USA)` },
    { value: 'partnership', label: `${t.step1.entityTypes.partnership} (USA)` },
    // Canada
    { value: 'incProvincial', label: isFr ? 'Société par actions provinciale (Canada)' : 'Provincial Corporation (Canada)' },
    { value: 'incFederal', label: isFr ? 'Société par actions fédérale (Canada)' : 'Federal Corporation (Canada)' },
    { value: 'generalPartnership', label: isFr ? 'Société en nom collectif — SENC (Canada)' : 'General Partnership (Canada)' },
    { value: 'limitedPartnership', label: isFr ? 'Société en commandite — SEC (Canada)' : 'Limited Partnership (Canada)' },
    { value: 'cooperative', label: isFr ? 'Coopérative (Canada)' : 'Cooperative (Canada)' },
    // UK
    { value: 'ltd', label: 'Private Limited Company — Ltd (UK)' },
    { value: 'plc', label: 'Public Limited Company — PLC (UK)' },
    { value: 'llp', label: 'Limited Liability Partnership — LLP (UK)' },
    // Germany
    { value: 'gmbh', label: 'GmbH (Germany)' },
    { value: 'ag', label: 'AG — Aktiengesellschaft (Germany)' },
    { value: 'ug', label: 'UG — haftungsbeschränkt (Germany)' },
    { value: 'ohg', label: 'OHG — Offene Handelsgesellschaft (Germany)' },
    { value: 'kg', label: 'KG — Kommanditgesellschaft (Germany)' },
    // Australia
    { value: 'ptyLtd', label: 'Proprietary Limited — Pty Ltd (Australia)' },
    { value: 'ltdAu', label: isFr ? 'Société publique — Ltd (Australie)' : 'Public Company — Ltd (Australia)' },
    { value: 'trust', label: isFr ? 'Fiducie commerciale — Trust (Australie)' : 'Business Trust (Australia)' },
    // Communes à tous les pays
    { value: 'soleProprietorship', label: t.step1.entityTypes.soleProprietorship },
    { value: 'other', label: t.step1.entityTypes.other },
  ];

  return (
    <div className="space-y-6">
      {/* CORRIGÉ — garde défensive supplémentaire (&& !isPublicMode) en
          plus de l'effet coupé plus haut : isBroker ne devrait de toute
          façon jamais devenir true en mode public puisque l'effet
          retourne avant de l'évaluer, mais on ne fait jamais confiance à
          une seule barrière pour une fuite de confidentialité. */}
      {isBroker && !isPublicMode && (
        <div className="space-y-2 p-4 rounded-md border border-border bg-muted/30">
          <Label>{(t.step1 as any)?.representedSeller || 'Represented Seller *'}</Label>
          <p className="text-xs text-muted-foreground">
            {(t.step1 as any)?.representedSellerHint || 'Select the seller you represent for this deal.'}
          </p>
          {representedSellers.length === 0 ? (
            <p className="text-sm text-destructive">
              {(t.step1 as any)?.noRepresentedSellers || 'No represented sellers found. Add a seller in your Broker Dashboard first.'}
            </p>
          ) : (
            <Select
              value={formData.general.representedSellerId}
              onValueChange={(val) => {
                const s = representedSellers.find(x => x.id === val);
                updateFormData('general', {
                  representedSellerId: val,
                  representedSellerName: s?.name || '',
                  representedSellerEmail: s?.email || '',
                });
                clearError('representedSellerId');
              }}
            >
              <SelectTrigger className={errors.representedSellerId ? 'border-destructive' : ''}>
                <SelectValue placeholder={(t.step1 as any)?.representedSellerPlaceholder || 'Select a represented seller'} />
              </SelectTrigger>
              <SelectContent>
                {representedSellers.map(s => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}{s.email ? ` — ${s.email}` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {errors.representedSellerId && (
            <p className="text-sm text-destructive">{errors.representedSellerId}</p>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="legalName">{t.step1.legalName} *</Label>
          <Input
            id="legalName"
            value={formData.general.legalName}
            onChange={(e) => handleChange('legalName', e.target.value)}
            placeholder={t.step1.legalNamePlaceholder}
            className={errors.legalName ? 'border-destructive' : ''}
          />
          {errors.legalName && <p className="text-sm text-destructive">{errors.legalName}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="tradeName">{t.step1.tradeName}</Label>
          <Input
            id="tradeName"
            value={formData.general.tradeName}
            onChange={(e) => handleChange('tradeName', e.target.value)}
            placeholder={t.step1.tradeNamePlaceholder}
          />
        </div>
      </div>

      {/* AJOUTÉ : Site web — le champ existait déjà dans le schéma
          (general.website) mais aucun input ne le collectait. Le CIM
          affichait donc systématiquement "—" pour ce champ. */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="website">{isFr ? "Site web de l'entreprise" : 'Company Website'}</Label>
          <Input
            id="website"
            type="url"
            value={formData.general.website}
            onChange={(e) => handleChange('website', e.target.value)}
            placeholder={isFr ? 'www.exemple.com' : 'www.example.com'}
          />
        </div>
        {/* AJOUTÉ : Devise du deal — absente du formulaire jusqu'ici alors
            qu'utilisée dans le CIM, le tableau de bord staff et l'évaluateur
            DSCR sans jamais avoir été réellement déclarée par le vendeur. */}
        <div className="space-y-2">
          <Label htmlFor="currency">{isFr ? 'Devise' : 'Currency'} *</Label>
          <Select
            value={formData.general.currency || 'CAD'}
            onValueChange={(value) => handleChange('currency', value)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="CAD">CAD — {isFr ? 'Dollar canadien' : 'Canadian Dollar'}</SelectItem>
              <SelectItem value="USD">USD — {isFr ? 'Dollar américain' : 'US Dollar'}</SelectItem>
              <SelectItem value="EUR">EUR — Euro</SelectItem>
              <SelectItem value="GBP">GBP — {isFr ? 'Livre sterling' : 'British Pound'}</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            {isFr
              ? "S'applique à tous les montants saisis dans ce formulaire."
              : 'Applies to all monetary figures entered in this form.'}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="entityType">{t.step1.entityType} *</Label>
          <Select
            value={formData.general.entityType}
            onValueChange={(value) => handleChange('entityType', value)}
          >
            <SelectTrigger className={errors.entityType ? 'border-destructive' : ''}>
              <SelectValue placeholder={t.step1.entityTypePlaceholder} />
            </SelectTrigger>
                       <SelectContent>
              {getEntityTypeOptions().map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.entityType && <p className="text-sm text-destructive">{errors.entityType}</p>}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="ownershipCount">{t.step1.ownershipCount}</Label>
            <Input
              id="ownershipCount"
              type="number"
              min={1}
              value={formData.general.ownershipCount}
              onChange={(e) => handleChange('ownershipCount', parseInt(e.target.value) || 1)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="primaryOwnerPercent">{t.step1.primaryOwnerPercent}</Label>
            <Input
              id="primaryOwnerPercent"
              type="number"
              min={0}
              max={100}
              value={formData.general.primaryOwnerPercent}
              onChange={(e) => handleChange('primaryOwnerPercent', parseInt(e.target.value) || 0)}
            />
          </div>
        </div>
      </div>
      {/* AJOUTÉ : Répéteur de propriétaires — pour les deals à plusieurs
          actionnaires (ex. BâtirPro : Marcel 65%, Lucie 25%, Émile 10%).
          Optionnel : si ownershipCount est à 1, ce détail n'est pas
          nécessaire et les champs ci-dessus suffisent. */}
      {formData.general.ownershipCount > 1 && (
        <div className="space-y-3 p-4 rounded-md border border-border bg-muted/30">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-base">{isFr ? 'Détail des propriétaires' : 'Ownership Detail'}</Label>
              <p className="text-xs text-muted-foreground mt-0.5">
                {isFr
                  ? 'Identifiez chaque actionnaire pour un deal à propriété partagée.'
                  : 'Identify each shareholder for a deal with shared ownership.'}
              </p>
            </div>
            <Button type="button" size="sm" variant="outline" onClick={addOwner}>
              <Plus className="h-4 w-4 mr-1" />
              {isFr ? 'Ajouter un propriétaire' : 'Add Owner'}
            </Button>
          </div>
          {owners.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">
              {isFr ? 'Aucun propriétaire ajouté pour le moment.' : 'No owners added yet.'}
            </p>
          ) : (
            <div className="space-y-3">
              {owners.map((owner) => (
                <div key={owner.id} className="grid grid-cols-1 md:grid-cols-6 gap-2 items-end p-3 rounded-md bg-background border border-border">
                  <div className="md:col-span-2 space-y-1">
                    <Label className="text-xs">{isFr ? 'Nom complet' : 'Full Name'}</Label>
                    <Input
                      value={owner.name}
                      onChange={(e) => updateOwner(owner.id, 'name', e.target.value)}
                      placeholder={isFr ? 'Nom du propriétaire' : "Owner's name"}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">%</Label>
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      value={owner.ownershipPercent || ''}
                      onChange={(e) => updateOwner(owner.id, 'ownershipPercent', parseInt(e.target.value) || 0)}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">{isFr ? 'Rôle' : 'Role'}</Label>
                    <Input
                      value={owner.role}
                      onChange={(e) => updateOwner(owner.id, 'role', e.target.value)}
                      placeholder={isFr ? 'Président, VP...' : 'President, VP...'}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Email</Label>
                    <Input
                      type="email"
                      value={owner.email}
                      onChange={(e) => updateOwner(owner.id, 'email', e.target.value)}
                    />
                  </div>
                  <div className="flex items-end gap-1">
                    <div className="flex-1 space-y-1">
                      <Label className="text-xs">{isFr ? 'Téléphone' : 'Phone'}</Label>
                      <Input
                        value={owner.phone}
                        onChange={(e) => updateOwner(owner.id, 'phone', e.target.value)}
                      />
                    </div>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="text-destructive shrink-0"
                      onClick={() => removeOwner(owner.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
              <p className={`text-xs text-right ${totalOwnershipPercent === 100 ? 'text-green-600' : 'text-amber-600'}`}>
                {isFr ? 'Total:' : 'Total:'} {totalOwnershipPercent}%
                {totalOwnershipPercent !== 100 && (isFr ? ' (devrait totaliser 100%)' : ' (should total 100%)')}
              </p>
            </div>
          )}
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="title">{t.step1.dealTitle} *</Label>
        <Input
          id="title"
          value={formData.general.title}
          onChange={(e) => handleChange('title', e.target.value)}
          placeholder={t.step1.dealTitlePlaceholder}
          className={errors.title ? 'border-destructive' : ''}
        />
        {errors.title && <p className="text-sm text-destructive">{errors.title}</p>}
      </div>
      <div className="space-y-2">
        <Label htmlFor="description">{t.step1.description} *</Label>
        <Textarea
          id="description"
          value={formData.general.description}
          onChange={(e) => handleChange('description', e.target.value)}
          placeholder={t.step1.descriptionPlaceholder}
          rows={4}
          className={errors.description ? 'border-destructive' : ''}
        />
        <div className="flex justify-between">
          <p className="text-xs text-muted-foreground">{t.step1.descriptionHint}</p>
          <p className={`text-xs ${formData.general.description.length >= 50 ? 'text-green-600' : 'text-muted-foreground'}`}>
            {formData.general.description.length} characters
          </p>
        </div>
        {errors.description && <p className="text-sm text-destructive">{errors.description}</p>}
      </div>

      <div className="space-y-2">
        <Label htmlFor="reasonForSale">{t.step1.reasonForSale}</Label>
        <Textarea
          id="reasonForSale"
          value={formData.general.reasonForSale}
          onChange={(e) => handleChange('reasonForSale', e.target.value)}
          placeholder={t.step1.reasonForSalePlaceholder}
          rows={3}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="industry">{t.step1.industry} *</Label>
          <Select
            value={formData.general.industry}
            onValueChange={(value) => handleChange('industry', value)}
          >
            <SelectTrigger className={errors.industry ? 'border-destructive' : ''}>
              <SelectValue placeholder={t.step1.industryPlaceholder} />
            </SelectTrigger>
            <SelectContent>
              {t.industries.map((industry) => (
                <SelectItem key={industry} value={industry}>{industry}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.industry && <p className="text-sm text-destructive">{errors.industry}</p>}
        </div>
        <div className="space-y-2">
          <Label htmlFor="naicsCode">{t.step1.naicsCode}</Label>
          <Input
            id="naicsCode"
            value={formData.general.naicsCode}
            onChange={(e) => handleChange('naicsCode', e.target.value)}
            placeholder={t.step1.naicsCodePlaceholder}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label htmlFor="city">{t.step1.city} *</Label>
          <Input
            id="city"
            value={formData.general.locationCity}
            onChange={(e) => handleChange('locationCity', e.target.value)}
            placeholder={t.step1.cityPlaceholder}
            className={errors.locationCity ? 'border-destructive' : ''}
          />
          {errors.locationCity && <p className="text-sm text-destructive">{errors.locationCity}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="state">{t.step1.state}</Label>
          <Input
            id="state"
            value={formData.general.locationState}
            onChange={(e) => handleChange('locationState', e.target.value)}
            placeholder={t.step1.statePlaceholder}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="country">{t.step1.country} *</Label>
          <Select
            value={formData.general.locationCountry}
            onValueChange={(value) => handleChange('locationCountry', value)}
          >
            <SelectTrigger className={errors.locationCountry ? 'border-destructive' : ''}>
              <SelectValue placeholder={t.step1.countryPlaceholder} />
            </SelectTrigger>
            <SelectContent>
              {t.countries.map((country) => (
                <SelectItem key={country} value={country}>{country}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.locationCountry && <p className="text-sm text-destructive">{errors.locationCountry}</p>}
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="yearEstablished">{t.step1.yearEstablished} *</Label>
        <Input
          id="yearEstablished"
          type="number"
          min={1900}
          max={new Date().getFullYear()}
          value={formData.general.yearEstablished}
          onChange={(e) => handleChange('yearEstablished', parseInt(e.target.value) || 2019)}
          className={errors.yearEstablished ? 'border-destructive' : ''}
        />
        <p className="text-xs text-muted-foreground">{t.step1.yearEstablishedHint}</p>
        {errors.yearEstablished && <p className="text-sm text-destructive">{errors.yearEstablished}</p>}
      </div>

      <div className="space-y-3">
        <Label>{t.step1.previouslyListed}</Label>
        <RadioGroup
          value={formData.general.previouslyListed ? 'yes' : 'no'}
          onValueChange={(value) => handleChange('previouslyListed', value === 'yes')}
          className="flex gap-6"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="yes" id="previouslyListed-yes" />
            <Label htmlFor="previouslyListed-yes" className="cursor-pointer font-normal">{t.step1.previouslyListedYes}</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="no" id="previouslyListed-no" />
            <Label htmlFor="previouslyListed-no" className="cursor-pointer font-normal">{t.step1.previouslyListedNo}</Label>
          </div>
        </RadioGroup>
        <div className="flex items-start gap-2 p-3 rounded-md bg-muted/50 border border-border">
          <Info className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
          <p className="text-xs text-muted-foreground">{t.step1.previouslyListedNote}</p>
        </div>
      </div>
    </div>
  );
};